import React, { useState, useEffect, useRef } from 'react';
import { Grid, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { WorkerCard, WorkerStatusHeader, WorkerStatusTabs, EmptyState, ProfileWarningModal } from './components';
import Header from 'modules/views/shared/Header';
import Pagination from 'modules/views/shared/Pagination';
import DeleteWorkerModal from './components/DeleteWorkerModal';
import FullscreenLoader from 'modules/views/shared/FullscreenLoader';
import WorkerStatusLoginModal, { WorkerStatusLoginModalType } from 'modules/views/shared/WorkerStatusLoginModal';
import Warning from 'modules/views/shared/Warning';

import useWorkerList from './hooks/useWorkerList';
import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { useAppSelector } from 'modules/state-mgmt/store';

import { useStyles } from './styles';

const WorkerStatus = () => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    workerList,
    workerCounts,
    isLoading,
    error,
    activeTab,
    page,
    paginationInfo,
    workerToDelete,
    handleTabChange,
    handlePageChange,
    setWorkerToDelete,
    onEditWorker,
    onUploadWorker,
  } = useWorkerList();

  const isSupervisorMode = useAppSelector(state => state.app.isSupervisorMode);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<IOnboardingState | null>(null);
  const [isProfileWarningModalVisible, setIsProfileWarningModalVisible] = useState(false);
  const isMountRef = useRef(true);

  // On mount, check if PIN was already verified (from navigation state) or if supervisor mode is on
  // This prevents PIN prompt when navigating from AdminMenu after PIN verification
  // or when supervisor mode is toggled off while on the page
  useEffect(() => {
    if (isMountRef.current) {
      const locationState = location.state as { pinVerified?: boolean };
      if (isSupervisorMode || locationState?.pinVerified) {
        setIsPinVerified(true);
      }
      isMountRef.current = false;
    }
  }, [isSupervisorMode, location.state]);

  const handleViewDetails = (worker: IOnboardingState) => {
    setSelectedWorker(worker);
    setIsProfileWarningModalVisible(true);
  };

  const handleCloseDetailsModal = () => {
    setIsProfileWarningModalVisible(false);
    setSelectedWorker(null);
  };

  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handlePageChange(value);
  };

  const renderWorkerStatusContent = () => {
    if (workerList.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }

    return (
      <Grid container spacing={3} className={classes.workersGrid} data-testid="worker-status-grid">
        {workerList.map(worker => (
          <Grid item xs={12} sm={6} md={4} key={worker.db?.id}>
            <WorkerCard
              worker={worker}
              onViewDetails={handleViewDetails}
              onEdit={() => onEditWorker(worker)}
              onDelete={() => setWorkerToDelete(worker)}
              onUploadData={onUploadWorker}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const showPinVerification = !isSupervisorMode && !isPinVerified;

  if (showPinVerification) {
    return (
      <div className={classes.container} data-testid="worker-status-pin-gate">
        {/* Blank background when PIN verification is required */}
        <div className={classes.blankBackground} />
        {/* PIN Verification Modal */}
        <WorkerStatusLoginModal
          type={WorkerStatusLoginModalType.ADMIN}
          hideCloseIcon={true}
          disableBackdropClose={true}
          onClose={() => {}}
          onSuccess={() => setIsPinVerified(true)}
        />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Header styleClass={classes.mainHeader} />

      {/* Header */}
      <WorkerStatusHeader />

      {/* Warning Message */}
      <Warning text={t('workerStatus.warningMessage')} styleClass={classes.warningMessage} />

      {/* Tabs */}
      <WorkerStatusTabs activeTab={activeTab} workerCounts={workerCounts} onTabChange={handleTabChange} />

      {/* Loading State */}
      {isLoading && <FullscreenLoader transparent={true} testId="worker-status-loader" />}

      {/* Error State */}
      {error && !isLoading && (
        <div className={classes.errorContainer} data-testid="worker-status-error">
          <Alert severity="error">{error}</Alert>
        </div>
      )}

      {/* Worker Status Content */}
      <div className={classes.content} data-testid="worker-status-content">
        {!error && renderWorkerStatusContent()}
      </div>

      {/* Pagination */}
      {!isLoading && !error && paginationInfo && paginationInfo.totalResults > 0 && (
        <div className={classes.paginationWrapper}>
          <Pagination
            count={paginationInfo.totalPages}
            page={page}
            siblingCount={1}
            boundaryCount={3}
            testId="worker-status-pagination"
            onChange={onPageChange}
          />
        </div>
      )}

      {/* Worker Details Modal */}
      {selectedWorker && (
        <ProfileWarningModal
          worker={selectedWorker}
          onEditWorker={onEditWorker}
          onClose={handleCloseDetailsModal}
          isVisible={isProfileWarningModalVisible}
        />
      )}

      {/* Delete Worker Modal */}
      {workerToDelete && (
        <DeleteWorkerModal
          worker={workerToDelete}
          onClose={() => setWorkerToDelete(null)}
          onDeleteSuccess={() => {
            setWorkerToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default WorkerStatus;
